// R and L (or A and P) at the edges of a section plane, projected with the camera, so a cut face is
// never shown without its sides named. RAS: +x is the subject's right.

import { useEffect, useRef } from 'react';
import { on } from '../viewport/events.ts';
import { useAtlas } from '../app/store.ts';
import type { ViewportEvent } from '../viewport/api.ts';

// Where the marks sit along a plane's edges, in RAS mm: just outside a template brain's extent.
const X = 80;
const Y_ANTERIOR = 80;
const Y_POSTERIOR = -114;
const Y_CENTRE = -17;
const Z_CENTRE = 15;

type Vec3 = [number, number, number];
type CameraEvent = Extract<ViewportEvent, { ev: 'camera' }>;

function project(m: Float32Array, p: Vec3, w: number, h: number): [number, number] | null {
  const cx = m[0]! * p[0] + m[4]! * p[1] + m[8]! * p[2] + m[12]!;
  const cy = m[1]! * p[0] + m[5]! * p[1] + m[9]! * p[2] + m[13]!;
  const cw = m[3]! * p[0] + m[7]! * p[1] + m[11]! * p[2] + m[15]!;
  if (cw <= 1e-6) return null;
  return [((cx / cw + 1) / 2) * w, ((1 - cy / cw) / 2) * h];
}

export function SectionMarks() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let last: CameraEvent | null = null;
    const draw = () => {
      const root = ref.current;
      if (!root) return;
      const plane = useAtlas.getState().section[0];
      if (!plane || !last) {
        root.style.display = 'none';
        return;
      }
      root.style.display = '';
      const w = root.clientWidth;
      const h = root.clientHeight;
      const pts: [string, Vec3][] =
        plane.axis === 'coronal'
          ? [
              ['R', [X, plane.mm, Z_CENTRE]],
              ['L', [-X, plane.mm, Z_CENTRE]],
            ]
          : plane.axis === 'axial'
            ? [
                ['R', [X, Y_CENTRE, plane.mm]],
                ['L', [-X, Y_CENTRE, plane.mm]],
              ]
            : [
                ['A', [plane.mm, Y_ANTERIOR, Z_CENTRE]],
                ['P', [plane.mm, Y_POSTERIOR, Z_CENTRE]],
              ];
      const marks = root.querySelectorAll<HTMLSpanElement>('span');
      pts.forEach(([letter, p], i) => {
        const el = marks[i];
        if (!el) return;
        const q = project(last!.viewProj, p, w, h);
        if (!q || q[0] < 0 || q[0] > w || q[1] < 0 || q[1] > h) {
          el.style.opacity = '0';
          return;
        }
        el.style.opacity = '1';
        el.textContent = letter;
        el.style.transform = `translate(${Math.round(q[0])}px, ${Math.round(q[1])}px) translate(-50%, -50%)`;
      });
    };
    const offCamera = on('camera', (e) => {
      last = e;
      draw();
    });
    const offStore = useAtlas.subscribe((s, prev) => {
      if (s.section !== prev.section) draw();
    });
    return () => {
      offCamera();
      offStore();
    };
  }, []);
  return (
    <div ref={ref} className="section-marks" aria-hidden="true" style={{ display: 'none' }}>
      <span />
      <span />
    </div>
  );
}
